import React, { useEffect, useState, forwardRef, useImperativeHandle } from 'react';
import { useTranslation } from 'react-i18next';
import { fetchWithAuth } from '../../utils/api';
import CreateInvoiceModal from '../../components/Sales/CreateInvoiceModal';
import { useMessages } from '../../components/Messages';
import { useAuth } from '../../contexts/AuthContext';
import { downloadCsv } from '../../utils/exportCsv';
import { isAdmin as checkAdmin } from '../../utils/roleUtils';
import './SalesOrders.css';
import { formatAppDate } from '../../utils/dateUtils';

const SalesOrdersList = forwardRef(({ onSelectOrder, selectedOrderId, onNew }, ref) => {
  const { t } = useTranslation('sales');
  const { showSuccess, showError } = useMessages();
  const { user } = useAuth();
  const isAdmin = checkAdmin(user);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showCreateInvoiceModal, setShowCreateInvoiceModal] = useState(false);
  const [selectedOrderForInvoice, setSelectedOrderForInvoice] = useState(null);

  useImperativeHandle(ref, () => ({
    fetchOrders
  }));

  useEffect(() => {
    fetchOrders();
  }, [statusFilter]);

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const response = await fetchWithAuth('/api/sales-orders/');
      if (response.ok) {
        const data = await response.json();
        setOrders(data.orders || []);
      } else {
        setError(t('salesOrders.list.messages.fetchFailed'));
      }
    } catch (error) {
      console.error('Error fetching sales orders:', error);
      setError(t('salesOrders.list.messages.loadingError'));
    } finally {
      setLoading(false);
    }
  };

  const filteredOrders = orders.filter(order => {
    const matchesSearch = (order.so_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          order.customer_name?.toLowerCase().includes(searchTerm.toLowerCase()));

    const matchesStatus = statusFilter === 'all' || order.status === statusFilter;

    return matchesSearch && matchesStatus;
  });

  const getStatusBadge = (status) => {
    const statusClass = status === 'CONFIRMED' ? 'status-completed' :
                       status === 'SHIPPED' ? 'status-success' :
                       status === 'INVOICED' ? 'status-success' :
                       status === 'DELIVERED' ? 'status-success' :
                       status === 'DRAFT' ? 'status-pending' :
                       status === 'CANCELLED' ? 'status-cancelled' :
                       status === 'ON_HOLD' ? 'status-warning' :
                       status === 'IN_PRODUCTION' ? 'status-active' :
                       status === 'ALLOCATED' ? 'status-info' :
                       status === 'PICKING' ? 'status-info' :
                       status === 'PACKED' ? 'status-info' : 'status-pending';

    const getStatusText = (status) => {
      switch(status) {
        case 'DRAFT': return t('salesOrders.view.statusOptions.draft');
        case 'CONFIRMED': return t('salesOrders.view.statusOptions.confirmed');
        case 'IN_PRODUCTION': return t('salesOrders.view.statusOptions.inProduction');
        case 'ALLOCATED': return t('salesOrders.view.statusOptions.allocated');
        case 'PICKING': return t('salesOrders.view.statusOptions.picking');
        case 'PACKED': return t('salesOrders.view.statusOptions.packed');
        case 'SHIPPED': return t('salesOrders.view.statusOptions.shipped');
        case 'INVOICED': return t('salesOrders.view.statusOptions.invoiced');
        case 'DELIVERED': return t('salesOrders.view.statusOptions.delivered');
        case 'CANCELLED': return t('salesOrders.view.statusOptions.cancelled');
        case 'ON_HOLD': return t('salesOrders.view.statusOptions.onHold');
        default: return t('salesOrders.view.statusOptions.draft');
      }
    };

    return <span className={`status-badge ${statusClass}`}>{getStatusText(status || 'DRAFT')}</span>;
  };

  const formatDate = (dateString) => {
    if (!dateString) return t('salesOrders.list.fallbackValues.na');
    return formatAppDate(dateString);
  };

  const formatCurrency = (amount) => {
    if (!amount) return '$0.00';
    return `$${parseFloat(amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const handleNewClick = () => {
    if (onNew) {
      onNew();
    }
  };

  const handleCreateInvoice = (order) => {
    setSelectedOrderForInvoice(order);
    setShowCreateInvoiceModal(true);
  };

  const handleInvoiceCreated = (invoice) => {
    // Refresh the orders list to update any status changes
    fetchOrders();
    // Show success message
    showSuccess(
      t('salesOrders.view.messages.invoiceCreated'),
      t('salesOrders.list.messages.invoiceCreatedSuccess', {
        invoiceNumber: invoice.invoice_number,
        orderNumber: selectedOrderForInvoice.so_number
      })
    );
    // Close modal and reset selection
    setShowCreateInvoiceModal(false);
    setSelectedOrderForInvoice(null);
  };

  const handleCloseInvoiceModal = () => {
    setShowCreateInvoiceModal(false);
    setSelectedOrderForInvoice(null);
  };

  if (loading && orders.length === 0) {
    return <div className="list-container"><div className="loading">{t('salesOrders.list.loading')}</div></div>;
  }

  return (
    <div className="list-container">
      <div className="list-header">
        <h2>{t('salesOrders.list.title')}</h2>
        <div className="header-actions">
          {isAdmin && (
            <button
              className="btn-secondary"
              onClick={() => downloadCsv('sales-orders', { status: statusFilter })}
              title={t('common:actions.export', 'Export CSV')}
            >
              CSV
            </button>
          )}
          <button className="btn-primary" onClick={handleNewClick}>
            <i className="icon">+</i> {t('salesOrders.list.buttons.newOrder')}
          </button>
        </div>
      </div>

      <div className="list-controls">
        <div className="search-box">
          <input
            type="text"
            placeholder={t('salesOrders.list.searchPlaceholder')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="filter-group">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="filter-select"
          >
            <option value="all">{t('salesOrders.list.filters.allStatus')}</option>
            <option value="DRAFT">{t('salesOrders.view.statusOptions.draft')}</option>
            <option value="CONFIRMED">{t('salesOrders.view.statusOptions.confirmed')}</option>
            <option value="IN_PRODUCTION">{t('salesOrders.view.statusOptions.inProduction')}</option>
            <option value="ALLOCATED">{t('salesOrders.view.statusOptions.allocated')}</option>
            <option value="PICKING">{t('salesOrders.view.statusOptions.picking')}</option>
            <option value="PACKED">{t('salesOrders.view.statusOptions.packed')}</option>
            <option value="SHIPPED">{t('salesOrders.view.statusOptions.shipped')}</option>
            <option value="INVOICED">{t('salesOrders.view.statusOptions.invoiced')}</option>
            <option value="DELIVERED">{t('salesOrders.view.statusOptions.delivered')}</option>
            <option value="CANCELLED">{t('salesOrders.view.statusOptions.cancelled')}</option>
            <option value="ON_HOLD">{t('salesOrders.view.statusOptions.onHold')}</option>
          </select>
        </div>
      </div>

      {error && <div className="error-message">{typeof error === 'string' ? error : t('salesOrders.list.messages.loadingError')}</div>}

      <div className="list-table">
        <table>
          <thead>
            <tr>
              <th>{t('salesOrders.list.tableHeaders.orderNumber')}</th>
              <th>{t('salesOrders.list.tableHeaders.customer')}</th>
              <th>{t('salesOrders.list.tableHeaders.total')}</th>
              <th>{t('salesOrders.list.tableHeaders.orderDate')}</th>
              <th>{t('salesOrders.list.tableHeaders.status')}</th>
            </tr>
          </thead>
          <tbody>
            {filteredOrders.length === 0 ? (
              <tr>
                <td colSpan="5" className="no-data">
                  {searchTerm ? t('salesOrders.list.messages.noOrdersFoundSearch') : t('salesOrders.list.messages.noOrdersFound')}
                </td>
              </tr>
            ) : (
              filteredOrders.map(order => (
                <tr
                  key={order.sales_order_id}
                  onClick={() => onSelectOrder(order.sales_order_id)}
                  className={selectedOrderId === order.sales_order_id ? 'selected' : ''}
                >
                  <td className="customer-code">
                    <code>{order.so_number || t('salesOrders.list.fallbackValues.na')}</code>
                  </td>
                  <td className="customer-name">
                    <strong>{order.customer_name || t('salesOrders.list.fallbackValues.unknown')}</strong>
                  </td>
                  <td className="text-right">
                    {formatCurrency(order.total_amount)}
                  </td>
                  <td>
                    {formatDate(order.order_date)}
                  </td>
                  <td>
                    {getStatusBadge(order.status)}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="list-footer">
        <div className="record-count">
          {t('salesOrders.list.messages.recordCount', {
            filtered: filteredOrders.length,
            total: orders.length
          })}
        </div>
      </div>

      {/* Invoice Creation Modal */}
      {showCreateInvoiceModal && selectedOrderForInvoice && (
        <CreateInvoiceModal
          salesOrder={selectedOrderForInvoice}
          onClose={handleCloseInvoiceModal}
          onInvoiceCreated={handleInvoiceCreated}
        />
      )}
    </div>
  );
});

SalesOrdersList.displayName = 'SalesOrdersList';

export default SalesOrdersList;